import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Phone, Send } from 'lucide-react';
import { PRODUCTS, CATEGORIES, Category, Product } from '../data/products';

// Shown first in the «Всі» view: newest pants
const FEATURED_IDS = ['130', '131', '135', '110', '111', '112', '113'];
import { Modal } from './ui/Modal';
import { ImageSlider } from './ui/ImageSlider';
const categoryLabels: Record<Category, string> = {
  All: 'Всі',
  Pants: 'Штани',
  Blouses: 'Блузи',
  Shirts: 'Сорочки',
  Jackets: 'Жакети',
  Dresses: 'Сукні',
  Tops: 'Майки',
  Vests: 'Жилети',
  Corsets: 'Корсети',
  Skirts: 'Спідниці'
};
export function Catalog() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const filteredProducts = activeCategory === 'All' ? [...PRODUCTS.filter(p => FEATURED_IDS.includes(p.id)).sort((a, b) => FEATURED_IDS.indexOf(a.id) - FEATURED_IDS.indexOf(b.id)), ...PRODUCTS.filter(p => !FEATURED_IDS.includes(p.id))] : PRODUCTS.filter(p => p.category === activeCategory);
  const handleContactClick = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    // In a real app, this might open a specific contact form or link
    window.location.href = `tel:+380964797313`;
  };
  return <section id="catalog" className="py-24 bg-white" data-id="element-36" data-mp="1mqklpb7nkts6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-37" data-mp="1hqyd5f2rs1pp">
        <div className="text-center mb-12" data-id="element-38" data-mp="1hsj30re52wh8">
          <h2 className="text-4xl md:text-5xl font-serif text-brand-dark mb-4" data-id="element-39" data-mp="1vimnpdxdei4k">
            Наш каталог
          </h2>
          <p className="text-brand-dark/70 max-w-2xl mx-auto mb-10" data-id="element-40" data-mp="1qqblu5j2km9d">
            Відкрийте для себе повний асортимент преміального одягу, створеного
            з увагою до кожної деталі.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3" data-id="element-41" data-mp="yy47jbr1dly5">
            {CATEGORIES.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-brand-burgundy text-white' : 'bg-brand-cream text-brand-dark hover:bg-brand-beige'}`} data-id="element-42" data-mp="2y346x5hlfq0u">
                {categoryLabels[category]}
              </button>)}
          </div>
        </div>

        {/* Product Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-43" data-mp="2k5fm1xnj0ivs">
          <AnimatePresence mode="popLayout" data-id="element-44" data-mp="2u33kaxkcc8ip">
            {filteredProducts.map(product => <motion.div key={product.id} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.3
          }} className="group cursor-pointer flex flex-col" onClick={() => setSelectedProduct(product)} data-id="element-45" data-mp="4ebaxu1a9ywa">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl mb-4 bg-brand-cream" data-id="element-46" data-mp="1a7pen4ie79nc">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover photo-grade transition-transform duration-700 group-hover:scale-105" data-id="element-47" data-mp="165chv5lmim2p" />
                  <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/10 transition-colors duration-300" data-id="element-48" data-mp="w9izh5l83yd0" />
                </div>

                <div className="flex-grow flex flex-col" data-id="element-49" data-mp="2ql62vgi6l750">
                  <div className="flex justify-between items-start mb-2" data-id="element-50" data-mp="2qjmnds4cn04a">
                    <h3 className="text-lg font-serif font-medium text-brand-dark pr-4" data-id="element-51" data-mp="1o8t0ivjf9j98">
                      {product.name}
                    </h3>
                    <span className="text-brand-burgundy font-medium whitespace-nowrap" data-id="element-52" data-mp="1eadbcy3ygk2d">
                      ₴{product.price}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4" data-id="element-53" data-mp="14k27fbk9uavh">
                    {product.sizes.map(size => <span key={size} className="text-xs px-2 py-1 bg-brand-cream text-brand-dark/70 rounded" data-id="element-54" data-mp="19bag25ds1s7l">
                        {size}
                      </span>)}
                  </div>

                  <button onClick={e => handleContactClick(e, product)} className="mt-auto w-full py-3 border border-brand-burgundy text-brand-burgundy rounded-full text-sm font-medium hover:bg-brand-burgundy hover:text-white transition-colors flex items-center justify-center gap-2" data-id="element-55" data-mp="llg4dr5729ox">
                    <MessageCircle className="w-4 h-4" data-id="element-56" data-mp="2ygorjtfrppy" />
                    Дізнатися / Замовити
                  </button>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Product Detail Modal */}
      <Modal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} data-id="element-57" data-mp="pwa5q5n8y8tv">
        {selectedProduct && <div className="flex flex-col md:flex-row h-full" data-id="element-58" data-mp="lvhyfhxxqe08">
            <div className="w-full md:w-1/2 h-[50vh] md:h-auto min-h-[400px] overflow-hidden rounded-t-xl md:rounded-l-xl md:rounded-tr-none" data-id="element-59" data-mp="zm3g53bnkhwj">
              {selectedProduct.images && selectedProduct.images.length > 1 ? <ImageSlider images={selectedProduct.images} alt={selectedProduct.name} data-id="element-60" data-mp="19lufpjf2ln3h" /> : <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover photo-grade" data-id="element-61" data-mp="1bq7fy2fzzo5c" />}
            </div>
            <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col" data-id="element-62" data-mp="1lm130frcf01d">
              <span className="text-sm uppercase tracking-wider text-brand-brown mb-2" data-id="element-63" data-mp="1qelqz0nkiiwz">
                {categoryLabels[selectedProduct.category]}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-brand-dark mb-4" data-id="element-64" data-mp="1zf0fe8vtwe4p">
                {selectedProduct.name}
              </h2>
              <p className="text-2xl text-brand-burgundy mb-6" data-id="element-65" data-mp="24f4vvywql8ux">
                ₴{selectedProduct.price}
              </p>

              <div className="prose prose-sm text-brand-dark/80 mb-8" data-id="element-66" data-mp="29cev269xgyvy">
                <p data-id="element-67" data-mp="a140jo0eb3zl">{selectedProduct.description}</p>
              </div>

              <div className="mb-8" data-id="element-68" data-mp="eyne0lp9rcqh">
                <h4 className="text-sm font-medium text-brand-dark mb-3" data-id="element-69" data-mp="ezykwue0h0lt">
                  Доступні розміри
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-70" data-mp="7hh4e47pwljc">
                  {selectedProduct.sizes.map(size => <span key={size} className="w-10 h-10 flex items-center justify-center border border-brand-beige rounded text-sm text-brand-dark" data-id="element-71" data-mp="3tv8nysax670l">
                      {size}
                    </span>)}
                </div>
              </div>

              {selectedProduct.colors && selectedProduct.colors.length > 0 && <div className="mb-8" data-id="element-72" data-mp="gft36tiqmpmv">
                  <h4 className="text-sm font-medium text-brand-dark mb-3" data-id="element-73" data-mp="gerehm8su4mo">
                    Доступні кольори
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-74" data-mp="316jthc4dczxt">
                    {selectedProduct.colors.map(color => <span key={color} className="px-3 py-1.5 border border-brand-beige rounded-full text-sm text-brand-dark bg-brand-cream" data-id="element-75" data-mp="2w6yux2t42tlm">
                        {color}
                      </span>)}
                  </div>
                </div>}

              <div className="mt-auto space-y-3" data-id="element-76" data-mp="3cx0nr0oj3760">
                <p className="text-sm text-brand-dark/60 mb-4 text-center" data-id="element-77" data-mp="3gvrcajexhghi">
                  Зв'яжіться з нами, щоб перевірити наявність та зробити
                  замовлення.
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-78" data-mp="dg2c9uli8zqo">
                  <a href="viber://chat?number=%2B380964797313" className="py-4 bg-[#7360F2] text-white rounded-full text-sm font-medium hover:bg-[#5f4dd6] transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-79" data-mp="dhn8dongwqqn">
                    <MessageCircle className="w-5 h-5" data-id="element-80" data-mp="rdh8pja6e7s5" />
                    Viber
                  </a>
                  <a href="https://t.me/+380964797313" target="_blank" rel="noopener noreferrer" className="py-4 bg-[#229ED9] text-white rounded-full text-sm font-medium hover:bg-[#1c8bc0] transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-81" data-mp="w84xzkd0767s">
                    <Send className="w-5 h-5" data-id="element-82" data-mp="2loqp1i58gwp2" />
                    Telegram
                  </a>
                </div>
                <a href="tel:+380964797313" className="w-full py-4 bg-brand-burgundy text-white rounded-full text-sm font-medium hover:bg-brand-burgundy/90 transition-colors flex items-center justify-center gap-2" data-id="element-83" data-mp="1xy3fytjl010x">
                  <Phone className="w-5 h-5" data-id="element-84" data-mp="22vwj1dpl8ewz" />
                  Зателефонувати для замовлення
                </a>
              </div>
            </div>
          </div>}
      </Modal>
    </section>;
}